import { logError } from '@databases/logs';
import { ObjectMode } from '@databases/types';
import { Content, ContentType } from '@databases/types/confluence';
import {
  BasicDatabase,
  Database,
  DatabaseOperations,
  ShallowDatabase,
} from '@databases/types/database';
import { IntlShape } from 'react-intl-next';

import { getContentById, searchContent } from '../confluence/content';
import { quoteCql, quoteFuzzyCql } from '../confluence/utils';
import { DATABASE_CONTENT_TYPE } from '../constants';
import {
  buildDatabaseFromCollabAndContent,
  buildDefaultValueConverters,
  getOrCreateCollabManager,
} from '../redux/collab/actions';
import { convertToYjs } from '../redux/collab/convert-to-collab';
import { MigrationManager } from '../redux/collab/migrations';
import { CommonRootState, SimpleStore } from '../redux/redux';

/** The REST API expansions that need to be made when fetching a shallow database. */
const SHALLOW_DATABASE_EXPANSIONS = [
  'body.storage',
  'space',
  'history.lastUpdated',
  'version',
  'children.attachment',
  'operations',
];

/** The REST API expansions that need to be made when fetching a database. */
const DATABASE_EXPANSIONS = [
  ...SHALLOW_DATABASE_EXPANSIONS,
  'container', // For cases where we select a database by entriesContentId, to find out the database contentId
];

const CreateCollabDatabaseMutation = `mutation CreateCollabDatabaseMutation($spaceKey: String!, $title: String) {
  createContentContextual(input: {spaceKey:$spaceKey, title: $title, status: CURRENT, type: "database"}) {
    id
  }
}`;

export async function createCollabDatabase(
  database: Database<ObjectMode.CREATE>,
  store: SimpleStore<CommonRootState>,
  intl: IntlShape
): Promise<Database> {
  let contentId: string;
  try {
    const json = await (
      await fetch('/cgraphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify([
          {
            query: CreateCollabDatabaseMutation,
            variables: {
              spaceKey: database.space.key,
              title: database.name, // don't want to add (Database) here
            },
          },
        ]),
      })
    ).json();

    if ('errors' in json[0] && json[0].errors.length > 0) {
      for (const error of json[0].errors) {
        if (
          error.message.indexOf('A database exists with the same title in this space') >
          -1
        ) {
          throw new Error(`A database exists with the same title in this space`);
        } else {
          throw new Error(
            `Could not create database. Got error from GraphQL: ${error.message}`
          );
        }
      }
    }

    contentId = json[0].data.createContentContextual.id;
  } catch (error: any) {
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error(`Unknown error while creating new database: ${error}`);
    }
  }

  const databaseContent = await getDatabaseContentByContentId<'database'>(contentId);

  const ydb = convertToYjs(database, buildDefaultValueConverters(store.getState), {});

  // set to the latest migration version
  ydb.doc.getMap('_meta').set('version', MigrationManager.getClientDocVersion());

  const newDatabase: Database = await new Promise(async (resolveDatabase) => {
    const manager = await store.dispatch(
      getOrCreateCollabManager(
        {
          contentId,
          spaceId: Number(databaseContent.space!.id), // we have space, it's expanded by getDatabaseContentByContentId
          referenceId: databaseContent.referenceId as string,
        },
        intl,
        ydb
      )
    );

    manager.onLoaded(async (db) => {
      const isReadOnly = manager.isReadOnly;

      resolveDatabase(
        await buildDatabaseFromCollabAndContent(databaseContent, db, isReadOnly)
      );
    });

    // kick off connection attempt
    manager.connect();
  });

  return newDatabase;
}

/**
 * Updates the database name for native content databases using the update title
 * GraphQL mutation.
 */
export async function updateDatabaseName(
  contentId: string,
  title: string
): Promise<void> {
  const UpdateTitleMutation = `mutation UpdateTitleMutation($contentId: ID!, $title: String) {
    updateTitle(contentId: $contentId, title: $title) {
      id
      title
    }
  }`;

  try {
    await fetch('/cgraphql', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify([
        {
          query: UpdateTitleMutation,
          variables: {
            contentId,
            title,
          },
        },
      ]),
    });
  } catch (error: unknown) {
    if (error instanceof Error) {
      logError('Error updating native database title', error);
      throw error;
    }
  }
}

export async function getDatabaseContentByContentId<T extends ContentType>(
  contentId: string
): Promise<Content<T>> {
  return await getContentById(contentId, DATABASE_EXPANSIONS);
}

export async function getDatabaseByContentId(
  contentId: string
): Promise<ShallowDatabase | BasicDatabase> {
  const content = await getContentById<'database'>(contentId, DATABASE_EXPANSIONS);
  if (!content) {
    throw new Error(`no content for database with content id ${contentId}`);
  }

  return getBasicCollabDatabaseFromContent(
    content,
    content.operations!.some(
      (op) => op.targetType === DATABASE_CONTENT_TYPE && op.operation === 'update'
    )
  );
}

async function searchDatabasesByCql(
  type: string,
  query?: string,
  spaceKey?: string
): Promise<(ShallowDatabase | BasicDatabase)[]> {
  const cql =
    [
      `type = ${quoteCql(type)}`,
      ...(spaceKey ? [`space=${quoteCql(spaceKey)}`] : []),
      `title~${quoteFuzzyCql(`${query}*`)}`,
    ].join(' and ') + ' order by lastModified desc';

  const results: BasicDatabase[] = [];
  for await (const content of searchContent(cql, SHALLOW_DATABASE_EXPANSIONS, 20)) {
    if (content.type === DATABASE_CONTENT_TYPE) {
      const database = getBasicCollabDatabaseFromContent(
        content,
        content.operations!.some(
          (op) => op.targetType === DATABASE_CONTENT_TYPE && op.operation === 'update'
        )
      );
      results.push(database);
    }
  }
  return results;
}

/**
 * Search for databases by search term in the whole Confluence instance. A maximum of 20 databases is returned. Broken
 * databases are ignored.
 * @param query
 * @param spaceKey If specified, only return global databases and databases in this space. Otherwise, return databases
 *                 in any space.
 */
export async function searchDatabases(
  query?: string,
  spaceKey?: string
): Promise<BasicDatabase[]> {
  return await searchDatabasesByCql(DATABASE_CONTENT_TYPE, query, spaceKey);
}

export function getBasicCollabDatabaseFromContent(
  content: Content<'database'>,
  isReadOnly: boolean
) {
  const operations: DatabaseOperations = {
    updateMetadata: !isReadOnly,
    updateEntries: !isReadOnly,
    delete: !isReadOnly,
  };

  const base: BasicDatabase = {
    contentId: content.id,
    contentVersion: content.version!.number,
    name: content.title,
    space: { key: content.space!.key, name: content.space!.name, id: content.space!.id },
    createdDate: content.history!.createdDate,
    createdBy: content.history!.createdBy,
    lastUpdatedDate: content.history!.lastUpdated!.when,
    lastUpdatedBy: content.history!.lastUpdated!.by,
    coverImage: undefined, // TODO
    operations,
    type: content.type,
  };

  return base;
}
